import {
  ClipAssetResponse,
  assetsAssetV2List,
  assetsClipCreate,
  assetsClipDestroy,
  assetsClipResetFullLengthClipCreate
} from '@goldcast/api/content';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import {
  ClipDeletes,
  ClipEdits
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { SourceType, CaptionStyle, CaptionLineConfig } from '@goldcast/player-config/src/types/asset-types';
import { Scenes } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import {
  DEFAULT_AUDIO_CONTENT_MAGIC_LAYOUT,
  DEFAULT_MAGIC_LAYOUT
} from '@goldcast/player-config/src/constants/clip-defaults';
import { core, isAudioContent as isAudioContentCoreStore } from '@/stores/core';
import { computeCaptionsWithData } from '@/App/remotion/libs/caption/caption';

export function isCustomUpload(): boolean {
  const content = core.getSnapshot().content;
  return content?.media_source_type === 'UPLOAD';
}

// TODO @@HiteshChoudhary0098 this needs to be updated to handle splits
export function createContentClip(
  start: number,
  end: number,
  source: SourceType,
  deletes: ClipDeletes = {},
  edits: ClipEdits = {},
  scenes?: Scenes,
  fps?: number,
  additionalParams?: Record<string, any>
): Promise<ClipAssetResponse> {
  const content = core.getSnapshot().content;
  const isAudio = isAudioContentCoreStore();
  const defaultLineConfig = CaptionLineConfig.OneLine;

  const startTimeAdjustedWithDeletes =
    Object.values(deletes).find(del => del.bounds[0] < start && del.bounds[1] > start)?.bounds[1] || start;

  const endTimeAdjustedWithDeletes =
    Object.values(deletes).find(del => del.bounds[0] < end && del.bounds[1] > end)?.bounds[0] || end;
  let updatedScenes: Scenes | undefined = undefined;
  if (fps && scenes) {
    const startTimeAdjustedWithDeletesFPS = startTimeAdjustedWithDeletes * fps;
    const endTimeAdjustedWithDeletesFPS = endTimeAdjustedWithDeletes * fps;
    updatedScenes = scenes
      .filter(
        scene => scene.toFrame >= startTimeAdjustedWithDeletesFPS && scene.fromFrame <= endTimeAdjustedWithDeletesFPS
      )
      .map(scene => {
        return {
          ...scene,
          fromFrame: Math.max(scene.fromFrame, startTimeAdjustedWithDeletesFPS),
          toFrame: Math.min(scene.toFrame, endTimeAdjustedWithDeletesFPS)
        };
      });
  }

  const { captions, duration, excludes } = computeCaptionsWithData({
    start: startTimeAdjustedWithDeletes,
    end: endTimeAdjustedWithDeletes,
    edits,
    deletes,
    merges: [],
    isTranslated: false
  });

  return assetsClipCreate({
    body: {
      content_id: content?.id as string,
      organization: content?.organization as string,
      asset_metadata: {
        start: startTimeAdjustedWithDeletes,
        end: endTimeAdjustedWithDeletes,
        size: 'LANDSCAPE',
        layout: isAudio ? 'AUDIOGRAM' : 'DEFAULT',
        source,
        deletes,
        edits,
        scenes: updatedScenes,
        highlight_type: 'text',
        subtitle: true,
        word_highlight_color: isAudio ? '#11184F' : '#330f80',
        magicLayout: isAudio ? DEFAULT_AUDIO_CONTENT_MAGIC_LAYOUT : DEFAULT_MAGIC_LAYOUT,
        is_edited: false,
        hide_borders: true,
        caption_styles: {
          scale: 1,
          animation: CaptionStyle.Outline,
          line_config: defaultLineConfig
        },
        captions,
        duration,
        excludes,
        ...additionalParams
      }
    }
  });
}

export function deleteContentClip(clipId: string): Promise<any> {
  return assetsClipDestroy({ id: clipId });
}

export function resetFullLengthClip(clipId: string): Promise<any> {
  return assetsClipResetFullLengthClipCreate({
    id: clipId,
    body: {}
  });
}

export function loadClips(broadcastId: string): Promise<Clip[]> {
  return assetsAssetV2List({
    queryParams: {
      contents: broadcastId,
      types: 'CLIP',
      limit: 1000,
      ordering: 'rank,-created_at'
    }
  }).then(({ results }) => results as unknown as Clip[]);
}
